import classnames from 'classnames';
import { useField } from '@unform/core';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import SelectField, { SelectChangeEvent } from '@mui/material/Select';
import { MdCancel } from 'react-icons/md';
import { FiChevronDown } from 'react-icons/fi';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';

import styles from './styles.module.scss';

export type typeOptions = {
  value: string | number;
  label: string;
};

export type SelectProps = {
  name: string;
  options: typeOptions[];
  placeholder: string;
  className?: string;
  defaultValue?: typeOptions['value'];
  label?: string;
  open?: boolean;
  value?: string;
  disabled?: boolean;
  onOpen?: (event: SyntheticEvent) => void;
  onClose?: (event: SyntheticEvent) => void;
  onChange?: (event: SelectChangeEvent) => void;
};

type Status = {
  variant: 'default' | 'error' | 'disabled';
};

export function SelectComponent({
  className,
  name,
  label,
  options,
  placeholder,
  open,
  value,
  disabled,
  onClose,
  onChange,
  onOpen,
  ...rest
}: SelectProps) {
  const selectRef = useRef(null);
  const { fieldName, registerField, defaultValue, error } = useField(name);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || '');
  const [selectVariant, setSelectVariant] = useState<Status['variant']>('default');

  const onChangeSelect = (event: SelectChangeEvent) => {
    setSelectedValue(event.target.value);
    if (onChange) onChange(event);
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current.children[1],
      getValue: (refs: HTMLInputElement) => refs.value,
      setValue: (refs: HTMLInputElement, newValue: string) => {
        refs.value = newValue;
        setSelectedValue(newValue);
      },
      clearValue: (refs: HTMLInputElement) => {
        refs.value = '';
        setSelectedValue('');
      }
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    if (error) {
      setSelectVariant('error');
    } else if (disabled) {
      setSelectVariant('disabled');
    } else {
      setSelectVariant('default');
    }
  }, [error, disabled]);

  return (
    <FormControl
      classes={{
        root: `${classnames(styles.selectContainer, {
          [styles[selectVariant]]: selectVariant,
          [className]: className
        })}`
      }}
    >
      {label && (
        <div className={styles.label}>
          <p className={styles.labelText}>{label}</p>
        </div>
      )}

      <SelectField
        name={name}
        ref={selectRef}
        onChange={onChangeSelect}
        defaultValue={defaultValue}
        className={styles.selectElement}
        classes={{ select: styles.selectValue }}
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        value={selectedValue}
        disabled={disabled}
        displayEmpty
        // eslint-disable-next-line react/no-unstable-nested-components
        IconComponent={() => <FiChevronDown />}
        {...rest}
      >
        <MenuItem value="" disabled style={{ display: 'none' }}>
          {placeholder}
        </MenuItem>
        {options &&
          options.map(item => (
            <MenuItem value={item.value} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
      </SelectField>
      {error && (
        <div className={styles.errorContent}>
          <Icon className={styles.errorIcon}>
            <MdCancel />
          </Icon>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
    </FormControl>
  );
}
